import { useEffect, useRef, useState, type ReactElement } from 'react';
import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

/** How long the reason tooltip lingers after a tap/click or after the pointer leaves. */
export const DISABLED_REASON_HIDE_DELAY_MS = 2500;

export interface DisabledReasonTooltipProps {
  /** Why the control is blocked — shown in the tooltip. */
  reason: string;
  /**
   * The "soft-disabled" trigger (a single button element that forwards ref and
   * props). It receives `aria-disabled="true"` + `data-disabled` and the
   * open/close handlers; it must NOT be natively `disabled` (that would kill
   * focus/click) and must not wire its own action.
   */
  children: ReactElement;
  /** @default 'top' */
  side?: 'top' | 'right' | 'bottom' | 'left';
  contentClassName?: string;
}

/**
 * Soft-disabled trigger + reason tooltip (Figma "click disabled button"): the
 * control looks disabled and never fires its action, but keeps focus/click so
 * the blocking reason can be surfaced — including on touch, where `title`
 * shows nothing. Radix already opens on focus/hover and wires
 * `aria-describedby` to the tooltip while open (unless the child sets its own);
 * we add tap-to-open and turn every Radix close request into a delayed one.
 *
 * Internal to the DS: shared by `FormFooter` and `ListItem`.
 */
export function DisabledReasonTooltip({
  reason,
  children,
  side = 'top',
  contentClassName,
}: DisabledReasonTooltipProps) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);

  function clearTimer() {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function scheduleClose() {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(false);
    }, DISABLED_REASON_HIDE_DELAY_MS);
  }

  useEffect(() => clearTimer, []);

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip
        open={open}
        onOpenChange={(next) => {
          if (next) {
            clearTimer();
            setOpen(true);
          } else {
            scheduleClose();
          }
        }}
      >
        <TooltipTrigger
          asChild
          aria-disabled="true"
          data-disabled=""
          onClick={(event) => {
            // Skip Radix's default click-to-close; a tap must SHOW the reason.
            event.preventDefault();
            clearTimer();
            setOpen(true);
            scheduleClose();
          }}
          onBlur={(event) => {
            event.preventDefault();
            clearTimer();
            setOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              clearTimer();
              setOpen(false);
            }
          }}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className={cn(
            'rounded-md border-0 bg-muted px-3 py-1.5 text-xs leading-4 tracking-[0.12px] text-foreground shadow-xs',
            contentClassName
          )}
        >
          {reason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
DisabledReasonTooltip.displayName = 'DisabledReasonTooltip';
